/**
 * One thread-status model for every surface that opens a conversation
 * (docs/proposals/workspaces.md §5) — the skill editor first; the chat pane and
 * the family builder join in their own stages.
 *
 * The agent's workspace gate answers a named thread one of four ways, and each
 * has its own next step, so none of them may collapse into an empty chat:
 *
 *   - `unavailable` — the server does not confirm it for this account or
 *     server (another account, another backend, deleted). Final: **Start new**.
 *   - `owner_mismatch` — it belongs to another workspace; open that one.
 *   - `no_local_workspace` — confirmed, but this device holds no record of it:
 *     the transcript can be read, the composer stays blocked.
 *   - `load_failed` — the transcript could not be fetched: **Retry**.
 */
import { AskError } from "./chat";
import { SkillsError } from "./skills";

/** Who owns a conversation — the agent's `WorkspaceOwner`, as far as the web reads it. */
export type ThreadOwner =
  | { kind: "chat" }
  | { kind: "workflow"; workflowId: string }
  | { kind: "skill"; vendor: string; skillId: string }
  | { kind: "family"; artifactId: string; revitVersion?: string };

export type ThreadStatus =
  | { kind: "ready" }
  | { kind: "unavailable" }
  | { kind: "owner_mismatch"; owner: ThreadOwner | null }
  | { kind: "no_local_workspace" }
  | { kind: "load_failed"; message: string };

export const THREAD_READY: ThreadStatus = Object.freeze({ kind: "ready" }) as ThreadStatus;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function ownerFrom(details: Record<string, unknown> | undefined): ThreadOwner | null {
  const owner = details?.["owner"];
  if (!isRecord(owner) || typeof owner["kind"] !== "string") return null;
  return owner as ThreadOwner;
}

/**
 * The status a refused turn or lookup puts the conversation in, or null when
 * the error is not about the thread at all (credits, the instance, a model).
 */
export function threadStatusFromError(error: unknown): ThreadStatus | null {
  let code: string | null = null;
  let details: Record<string, unknown> | undefined;
  if (error instanceof AskError) {
    code = error.code;
    details = error.details;
  } else if (error instanceof SkillsError) {
    code = error.parsed.code;
    details = error.parsed.details;
  }
  switch (code) {
    case "THREAD_NOT_AVAILABLE":
      return { kind: "unavailable" };
    case "THREAD_OWNER_MISMATCH":
      return { kind: "owner_mismatch", owner: ownerFrom(details) };
    case "NO_LOCAL_WORKSPACE":
      return { kind: "no_local_workspace" };
    default:
      return null;
  }
}

/** Whether the composer may send into the conversation in this status. */
export function canSendInThread(status: ThreadStatus): boolean {
  return status.kind === "ready";
}

/** The locale key of the sentence each non-ready status shows. */
export function threadStatusKey(status: ThreadStatus): string | null {
  switch (status.kind) {
    case "ready":
      return null;
    case "unavailable":
      return "threadStatus.unavailable";
    case "owner_mismatch":
      return "threadStatus.ownerMismatch";
    case "no_local_workspace":
      return "threadStatus.noLocalWorkspace";
    case "load_failed":
      return "threadStatus.loadFailed";
  }
}
